import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contractTemplateApi } from '../../api/contracts';
import { ContractTemplate } from '../../types/contracts';
import { ApiError } from '../../api/client';
import { IconActionButton } from '../../components/IconActionButton';
import { Pencil, Power } from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';

export function ContractTemplatesPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ContractTemplate | null>(null);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const templatesQuery = useQuery({ queryKey: ['contract-templates'], queryFn: () => contractTemplateApi.list() });
  const { register, handleSubmit, reset } = useForm<{ name: string; category?: string; templateContent: string }>();
  const editForm = useForm<{ name: string; category?: string; templateContent: string }>({
    values: editing ? { name: editing.name, category: editing.category ?? undefined, templateContent: editing.template_content } : undefined,
  });

  const createMutation = useMutation({
    mutationFn: (values: { name: string; category?: string; templateContent: string }) => contractTemplateApi.create(values),
    onSuccess: () => { showSuccess('Contract template created.'); queryClient.invalidateQueries({ queryKey: ['contract-templates'] }); setModalOpen(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create template.'),
  });

  const updateMutation = useMutation({
    mutationFn: (values: { name: string; category?: string; templateContent: string }) => contractTemplateApi.update(editing!.id, values),
    onSuccess: () => { showSuccess('Template updated.'); queryClient.invalidateQueries({ queryKey: ['contract-templates'] }); setEditing(null); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to update template.'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => contractTemplateApi.deactivate(id),
    onSuccess: () => { showSuccess('Template deactivated.'); queryClient.invalidateQueries({ queryKey: ['contract-templates'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to deactivate template.'),
  });

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <PageHeader
        title="Contract Templates"
        description="Use merge fields like {{staff_name}}, {{position}}, {{department}}, {{duty_station}}, {{start_date}}, {{end_date}}, {{grade}}, {{staff_id}} - these are automatically replaced with real staff information when a contract is created."
        actions={<button className="btn-primary" onClick={() => setModalOpen(true)}>New Template</button>}
      />

      {templatesQuery.data && (
        <DataTable
          rows={templatesQuery.data.data}
          rowKey={(t) => t.id}
          emptyMessage="No contract templates yet."
          columns={[
            { header: 'Name', render: (t) => t.name },
            { header: 'Category', render: (t) => t.category ?? '-' },
            { header: '', render: (t) => <span className="icon-action-group"><IconActionButton icon={Pencil} label="Edit" onClick={() => setEditing(t)} /> <IconActionButton icon={Power} label="Deactivate" onClick={() => deactivateMutation.mutate(t.id)} /></span> },
          ]}
        />
      )}

      {editing && (
        <Modal title={`Edit: ${editing.name}`} onClose={() => setEditing(null)}>
          <form onSubmit={editForm.handleSubmit((values) => updateMutation.mutate(values))}>
            <label>Name<input {...editForm.register('name', { required: true })} /></label>
            <label>Category<input {...editForm.register('category')} /></label>
            <label>Template Content<textarea rows={8} {...editForm.register('templateContent', { required: true })} /></label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={updateMutation.isPending}>{updateMutation.isPending ? 'Saving…' : 'Save'}</button><button type="button" className="btn-secondary" onClick={() => setEditing(null)}>Cancel</button></div>
          </form>
        </Modal>
      )}

      {modalOpen && (
        <Modal title="New Contract Template" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>Name<input {...register('name', { required: true })} /></label>
            <label>Category<input {...register('category')} /></label>
            <label>Template Content<textarea rows={8} {...register('templateContent', { required: true })} /></label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Saving…' : 'Create'}</button><button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
